import React, { useState } from "react";
import { Box, TextField, Typography, Button, Chip, Grid, MenuItem, Select, FormControl } from "@mui/material";
import { FlowTask, TaskStatus, TaskPriority, /* TaskCategory */ } from "../../interfaces/flowTask.interface";
import { useCreateTodoTask } from "../../hooks/useCreateTodoTask";
import { TASK_STATUSES, TASK_PRIORITIES } from "../../constants";
import { colors } from "../../../ui/colors";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";

interface CreateTaskFormProps {
  kanbanCategoryUid: string | null;
  initialStatus?: TaskStatus;
  onSuccess?: () => void;
  isPreviewMode?: boolean;
}

export const CreateTaskForm = ({ kanbanCategoryUid, initialStatus, onSuccess, isPreviewMode = false }: CreateTaskFormProps) => {

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  const [status, setStatus] = useState<TaskStatus>(initialStatus || "TODO");
  const [priority, setPriority] = useState<TaskPriority>("MEDIUM");
  // const [category, setCategory] = useState<TaskCategory>("JOB");
  const [deadline, setDeadline] = useState("");
  const [titleError, setTitleError] = useState("");



  const createTaskMutation = useCreateTodoTask();
  const { showSnackbar } = useGlobalSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate title
    if (!title.trim()) {
      setTitleError("Title is required");
      return;
    }

    if (!kanbanCategoryUid) {
      showSnackbar("You must select a Category/Section first.", "error", 6000, "bottom-left");
      return;
    }

    const taskData: Omit<FlowTask, "uid" | "createdAt" | "updatedAt"> = {
      kanbanCategoryUid: kanbanCategoryUid,
      name: title.trim(),
      description: notes.trim() || undefined,
      status,
      priority,
      // category,
      deadline: deadline || undefined,
    };

    if (isPreviewMode) {
      // Fake successful creation
      setTitle("");
      setNotes("");
      setStatus("TODO");
      setPriority("MEDIUM");
      setDeadline("");
      setTitleError("");
      showSnackbar("Task created successfully (Preview Mode)!", "success", 5000, "bottom-left");
      onSuccess?.();
      return;
    }

    try {
      await createTaskMutation.mutateAsync(taskData);
      // Reset form
      setTitle("");
      setNotes("");
      setStatus("TODO");
      setPriority("MEDIUM");
      // setCategory("JOB");
      setDeadline("");
      setTitleError("");

      // Show success snackbar
      showSnackbar("Task created successfully!", "success", 5000, "bottom-left");

      onSuccess?.();
    } catch (error) {
      console.error("Error creating task:", error);

      // Show error snackbar
      const errorMessage = error instanceof Error ? error.message : "Failed to create task. Please try again.";
      showSnackbar(errorMessage, "error", 6000, "bottom-left");
    }
  };


  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: 2.5,
        py: 1,
        px: 2,
      }}
    >
      {/* Task Information */}
      <Grid container spacing={2}>
        {/* Title */}
        <Grid item xs={12}>
          <TextField
            placeholder="Task Title"
            required
            fullWidth
            variant="outlined"
            size="small"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError("");
            }}
            error={!!titleError}
            helperText={titleError}
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1rem",
                fontWeight: 500,
                borderRadius: 1.5,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                "& fieldset": { borderColor: "var(--fx-surface-border, #E2E8F0)" },
                "&:hover fieldset": {
                  borderColor: colors.orange?.base || "#f97316",
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.orange?.base || "#f97316",
                  borderWidth: "1px",
                },
              },
            }}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            placeholder="Add notes or details..."
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                borderRadius: 1.5,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                "& fieldset": { borderColor: "var(--fx-surface-border, #E2E8F0)" },
                "&:hover fieldset": {
                  borderColor: colors.orange?.base || "#f97316",
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.orange?.base || "#f97316",
                  borderWidth: "1px",
                },
              },
            }}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: colors.gray?.[500] }}>
              Status
            </Typography>
            <Select
              native
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
              sx={{
                borderRadius: 1.5,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--fx-surface-border, #E2E8F0)" },
                fontSize: "0.875rem",
              }}
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Priority */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: colors.gray?.[500] }}>
              Priority
            </Typography>
            <Select
              native
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              sx={{
                borderRadius: 1.5,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--fx-surface-border, #E2E8F0)" },
                fontSize: "0.875rem",
              }}
            >
              {TASK_PRIORITIES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Deadline */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: colors.gray?.[500] }}>
              Deadline
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  "& fieldset": { borderColor: "var(--fx-surface-border, #E2E8F0)" },
                  fontSize: "0.875rem",
                },
              }}
            />
          </FormControl>
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!title.trim() || (createTaskMutation.isPending && !isPreviewMode) || !kanbanCategoryUid}
          sx={{
            bgcolor: colors.orange?.base || "#f97316",
            "&:hover": {
              bgcolor: colors.orange?.dark || "#ea580c",
            },
            boxShadow: "none",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 0.75,
          }}
        >
          {createTaskMutation.isPending && !isPreviewMode ? "Creating..." : "Create Task"}
        </Button>
      </Box>
    </Box>
  );
};
